import { useState } from 'react';
import {
  collectSignals,
  evaluateDeviceCheck,
  isDeviceReady,
  requestPersistentStorage,
  type CheckResult,
  type CheckStatus,
} from '../pos/deviceCheck';
import type { PosHandle } from '../pos/usePos';
import { Alert, Button, Card } from './ui';

const STATUS_TEXT: Record<CheckStatus, string> = {
  ok: 'In Ordnung',
  warn: 'Achtung',
  fail: 'Fehler',
};
const STATUS_SYMBOL: Record<CheckStatus, string> = { ok: '✓', warn: '!', fail: '✗' };

/** Prüft die Erreichbarkeit des Servers mit der öffentlichen Versionsroute (ohne Anmeldung, ohne Datenbankzugriff). */
async function probeServer(): Promise<boolean> {
  const response = await fetch('/api/v1/version', { cache: 'no-store' });
  return response.ok;
}

/**
 * Geräte-Check „kassenbereit“ (SYN-13): am Vorabend ausführen. Zeigt Ampeln mit Text (nicht nur Farbe) und sagt am Ende,
 * ob das Gerät bereit ist. Warnungen dürfen bleiben, Fehler nicht.
 */
export function DeviceCheck({ pos }: { pos: PosHandle }) {
  const [results, setResults] = useState<CheckResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [persistFailed, setPersistFailed] = useState(false);

  async function run() {
    if (pos.store === null) return;
    setBusy(true);
    try {
      const signals = await collectSignals({ store: pos.store, pending: pos.pending, probeServer });
      setResults(evaluateDeviceCheck(signals));
    } finally {
      setBusy(false);
    }
  }

  async function protect() {
    setPersistFailed(!(await requestPersistentStorage()));
    await run();
  }

  const ready = results === null ? null : isDeviceReady(results);
  const unprotected = results?.some(
    (check) => check.id === 'storage-persist' && check.status === 'warn',
  );

  return (
    <section className="stack" aria-labelledby="geraetecheck">
      <h2 id="geraetecheck">Geräte-Check</h2>
      <Card>
        <div className="stack">
          <p className="muted">
            Am Vorabend ausführen: Sie sehen, ob dieses Gerät am Basar-Tag ohne Netz durchhält.
          </p>
          <div>
            <Button variant="secondary" busy={busy} onClick={() => void run()}>
              Geräte-Check starten
            </Button>
          </div>
          {ready !== null && (
            <Alert kind={ready ? 'success' : 'error'}>
              {ready
                ? 'Dieses Gerät ist kassenbereit.'
                : 'Dieses Gerät ist noch nicht kassenbereit. Bitte die Fehler beheben.'}
            </Alert>
          )}
          {results !== null && (
            <ul className="list" aria-label="Ergebnisse des Geräte-Checks">
              {results.map((check) => (
                <li key={check.id}>
                  <div>
                    <strong>
                      <span aria-hidden="true">{STATUS_SYMBOL[check.status]}</span> {check.label}
                      <span className="badge">{STATUS_TEXT[check.status]}</span>
                    </strong>
                    <div className="muted">{check.detail}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {unprotected === true && (
            <div className="stack">
              <Button icon="shield" variant="secondary" small onClick={() => void protect()}>
                Speicher schützen
              </Button>
              {persistFailed && (
                <p className="muted">
                  Der Browser hat das abgelehnt. Installieren Sie die App auf dem Home-Bildschirm.
                </p>
              )}
            </div>
          )}
        </div>
      </Card>
    </section>
  );
}
